// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useState, useEffect } from 'react';
import { PhotoSlider } from 'react-photo-view';
import { ReactPhotoSphereViewer } from 'react-photo-sphere-viewer';
import 'react-photo-view/dist/react-photo-view.css';

// ==============================|| MUI IMPORTS ||============================== //
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material';

// ==============================|| ANT DESIGN ICONS IMPORTS ||============================== //
import { CloseOutlined, DownloadOutlined, LeftOutlined, RightOutlined, EditOutlined } from '@ant-design/icons';

// ==============================|| TYPES IMPORTS ||============================== //
import { ImagePreviewProps, PreviewImageItem, TabularFileItem } from 'types/CommonFileManager/commonFileManager';



// ==============================|| CONTEXT & HOOKS ||============================== //
import { useFileManagerState } from 'hooks/useFileManager';



const ImagePreview: React.FC<ImagePreviewProps> = ({
    images: propImages,
    currentIndex: propCurrentIndex,
    isOpen: propIsOpen,
    onClose: propOnClose,
    onIndexChange: propOnIndexChange,
    onRename: propOnRename,
    onDownload: propOnDownload,
    allowEdit: propAllowEdit
}) => {
    const state = useFileManagerState();

    const images = propImages ?? state.previewImages;
    const currentIndex = propCurrentIndex ?? state.previewIndex;
    const isOpen = propIsOpen ?? state.previewImages.length > 0;
    const onClose = propOnClose ?? (() => state.setPreviewImages([]));
    const onIndexChange = propOnIndexChange ?? state.setPreviewIndex;
    const onRename = propOnRename ?? state.handleEditFileRename;
    const onDownload = propOnDownload ?? state.handleDownloadFile;
    const [is360, setIs360] = useState(false);
    const currentFile = images[currentIndex];

    const allowEdit = propAllowEdit ?? (state.allowEdit && currentFile && state.canManipulateItem(currentFile));

    useEffect(() => {
        if (currentFile && isOpen) {
            const img = new Image();
            img.src = currentFile.url || '';

            img.onload = () => {
                const { naturalWidth: w, naturalHeight: h } = img;
                const ratio = w / h;
                const isRatio360 = ratio > 1.9 && ratio < 2.1;
                setIs360(isRatio360);
            };

            img.onerror = () => {
                setIs360(false);
            };
        } else {
            setIs360(false);
        }
    }, [currentFile, isOpen]);

    if (!currentFile || !isOpen) return null;

    const handleClose = () => {
        setIs360(false);
        onClose();
    };

    const handleNext = () => {
        if (currentIndex < images.length - 1) {
            onIndexChange(currentIndex + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            onIndexChange(currentIndex - 1);
        }
    };

    const Header = () => (
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            pointerEvents: 'none',
            p: 2
        }}>
            {/* Left: Title & Count */}
            <Box sx={{
                bgcolor: 'rgba(0,0,0,0.5)',
                px: 2,
                py: 1,
                borderRadius: '8px',
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
            }}>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                    {currentFile.name}
                </Typography>
                {allowEdit && onRename && (
                    <Tooltip title="Rename">
                        <IconButton
                            size="small"
                            onClick={() => onRename(currentFile)}
                            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <EditOutlined />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Right: Actions */}
            <Box sx={{
                display: 'flex',
                gap: 1,
                pointerEvents: 'auto',
                bgcolor: 'rgba(0,0,0,0.5)',
                p: 0.5,
                borderRadius: '8px'
            }}>
                {onDownload && (
                    <Tooltip title="Download">
                        <IconButton onClick={() => onDownload(currentFile)} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                            <DownloadOutlined />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Close">
                    <IconButton onClick={handleClose} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                        <CloseOutlined />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );

    const NavigationButtons = () => (
        <>
            {currentIndex > 0 && (
                <IconButton
                    onClick={handlePrev}
                    sx={{
                        position: 'absolute',
                        left: 20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10000,
                        color: 'white',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                        width: 50,
                        height: 50
                    }}
                >
                    <LeftOutlined style={{ fontSize: '24px' }} />
                </IconButton>
            )}
            {currentIndex < images.length - 1 && (
                <IconButton
                    onClick={handleNext}
                    sx={{
                        position: 'absolute',
                        right: 20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10000,
                        color: 'white',
                        bgcolor: 'rgba(0,0,0,0.3)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                        width: 50,
                        height: 50
                    }}
                >
                    <RightOutlined style={{ fontSize: '24px' }} />
                </IconButton>
            )}
        </>
    );

    if (is360) {
        return (
            <Dialog
                fullScreen
                open={isOpen}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                }}
            >
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    bgcolor: 'black',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Header />
                    <NavigationButtons />
                    <ReactPhotoSphereViewer
                        src={currentFile.url || ''}
                        height="100vh"
                        width="100%"
                        container=""
                    />
                </Box>
            </Dialog>
        );
    }

    const sliderImages = images.map(img => ({ src: img.url || '', key: img.id }));

    return (
        <PhotoSlider
            images={sliderImages}
            visible={isOpen}
            onClose={handleClose}
            index={currentIndex}
            onIndexChange={onIndexChange}
            bannerVisible={false}
            maskClosable={false}
            overlayRender={() => (
                <>
                    <Header />
                    <NavigationButtons />
                </>
            )}
            toolbarRender={() => null}
        />
    );
};

export default ImagePreview;
